import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { PagedResponse } from 'projects/internal/r1-component-library/src/lib/models/appcloud/paged-response.model';
import { App } from 'projects/internal/r1-component-library/src/lib/models/appcloud/app.model';
import { AdminHttpService } from './utils/admin-http.service';
import { Subscription, SubscriptionPatch } from '../models/subscription.model';

/**
 * Subscriptions (= `Applicense` rows) for the UXPin "Apps" screens, read through
 * `/appcloud/admin/applicenses` (cross-account, paged, `app` embedded).
 */
@Injectable({
    providedIn: 'root',
})
export class SubscriptionsService {
    readonly #http = inject(AdminHttpService);

    readonly #licensesUrl = `${this.#http.adminUrl}/applicenses`;
    readonly #appsUrl = `${this.#http.adminUrl}/apps`;

    /**
     * `GET /appcloud/admin/applicenses?accountId={id}` with `app` embedded.
     * TODO(backend): billing fields (`autoRenew`, `renewalDate`, `billingAmount`, `billingPeriod`,
     * `billingNote`, `quantity`) are not on `Applicense` yet.
     */
    findByAccount(accountId: number): Observable<Subscription[]> {
        return this.#http
            .get<PagedResponse<Subscription>>(this.#licensesUrl, {
                process: 'SubscriptionsService.findByAccount',
                message: 'Loading subscriptions...',
                errorMessage: 'An error occurred while retrieving subscriptions. Please try again later.',
                params: { accountId, page: 0, pageSize: 1000, sortField: 'usageEndDate', sortOrder: 'desc' },
            })
            .pipe(map(page => page?.content ?? []));
    }

    /** `GET /appcloud/admin/applicenses/{id}` with `app` embedded. */
    get(id: number): Observable<Subscription> {
        return this.#http.get<Subscription>(`${this.#licensesUrl}/${id}`, {
            process: 'SubscriptionsService.get',
            message: 'Loading subscription...',
            errorMessage: 'An error occurred while retrieving the subscription. Please try again later.',
        });
    }

    /** `PATCH /appcloud/admin/applicenses/{id}` (merge-patch) — "Edit Subscription". */
    patch(id: number, patch: SubscriptionPatch): Observable<Subscription> {
        return this.#http.patch<Subscription>(`${this.#licensesUrl}/${id}`, patch, {
            process: 'SubscriptionsService.patch',
            message: 'Updating subscription...',
            errorMessage: 'An error occurred while updating the subscription. Please try again later.',
        });
    }

    /** TODO(backend): `POST /appcloud/admin/applicenses/{id}/renew` — "Renew Subscription" (not in the admin controller yet). */
    renew(id: number): Observable<Subscription> {
        return this.#http.post<Subscription>(
            `${this.#licensesUrl}/${id}/renew`,
            {},
            {
                process: 'SubscriptionsService.renew',
                message: 'Renewing subscription...',
                errorMessage: 'An error occurred while renewing the subscription. Please try again later.',
            }
        );
    }

    /** Apps catalogue (`GET /appcloud/admin/apps`), used for "Recommended Apps". */
    findApps(): Observable<App[]> {
        return this.#http
            .get<PagedResponse<App>>(this.#appsUrl, {
                process: 'SubscriptionsService.findApps',
                message: 'Loading apps...',
                errorMessage: 'An error occurred while retrieving apps. Please try again later.',
                params: { page: 0, pageSize: 1000, sortField: 'name' },
            })
            .pipe(map(page => page?.content ?? []));
    }
}
